import { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { font, palette, zIndex } from 'styles/Global/globalStyles';
import { useLoginStore } from 'store/useLoginDataStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { pagePaths } from 'constants/path';
import useToggle from 'hooks/CustomHooks/useToggle';
import TodayAlarmDropdown from 'components/Dropdown/TodayAlarmDropdown';
import { useAlarmListStore, useGlobalToastStore } from 'store/useToastStore';
import UnConfirmAlarmIcon from 'assets/Icons/Header/UnConfirmAlarmIcon';
import { GlobalToast } from 'components/Toast/ToastMessage';
import { MessagePayload, onMessage } from 'firebase/messaging';
import { waitForMessaging } from 'firebaseConfig';
import moment from 'moment';
import { calculateAge } from 'utils/calculateAge';
import { formattedTime } from 'utils/calculateTime';
import Logout from 'assets/Icons/Header/Logout';
import Alarm from 'assets/Icons/Alarm/Alarm';
import AlarmFill from 'assets/Icons/Alarm/AlarmFill';
import SettingUser from 'assets/Icons/Header/SettingUser';
import { useLeaveModalStateStore } from 'store/useLeaveModalStateStore';
import { accountCodeFilter, diagnosisAndAssessmentCodeFilter, doctorCodeFilter, patientCodeFilter, userCodeFilter } from 'utils/toastMessageCodeFilter';
import { HandleFCMFilter } from 'models/Hospital/DCKHeaderTypes';
import { isConsultationHospital } from 'utils/hospitalUtils';

const Header = () => {
    const { loginData } = useLoginStore();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // const [isLogoutModal, setIsLogoutModal] = useToggle();
    const [isAlarmDropdown, setIsAlarmDropdown] = useToggle();

    const { unConfirmedAlarmList, removeOldAlarms, resetUnConfirmedAlarmList } = useAlarmListStore();
    const { setToast } = useGlobalToastStore();
    const { editState, movePageState, setIsLeaveModal, setClickType, setMovePath, setIsLogoutModal } = useLeaveModalStateStore();

    // FCM 메세지 필터링
    const handleFCMFilter: HandleFCMFilter = useCallback((payload) => {
        if (!payload.data) return;

        if (diagnosisAndAssessmentCodeFilter(payload.data.code)) {
            return {
                addTime: moment().format('A h:mm'),
                diagnosisTime: formattedTime(payload.data.diagnosisDateTime!),
                patientGender: payload.data.patientGender,
                patientIdentityNumber: calculateAge(payload.data.patientIdentityNumber!).birthDate,
                patientName: payload.data.patientName,
                regNum: payload.data.regNum,
                code: payload.data.code,
            };
        } else if (userCodeFilter(payload.data.code)) {
            return {
                addTime: moment().format('A h:mm'),
                code: payload.data.code,
            };
        } else if (doctorCodeFilter(payload.data.code)) {
            return { doctorName: payload.data.doctorName, addTime: moment().format('A h:mm'), code: payload.data.code };
        } else if (patientCodeFilter(payload.data.code)) {
            return {
                regNum: payload.data.regNum,
                patientName: payload.data.patientName,
                patientGender: payload.data.patientGender,
                patientIdentityNumber: calculateAge(payload.data.patientIdentityNumber!).birthDate,
                addTime: moment().format('A h:mm'),
                code: payload.data.code,
            };
        } else if (accountCodeFilter(payload.data.code)) {
            return { userName: payload.data.userName, userLevel: payload.data.userLevel, addTime: moment().format('A h:mm'), code: payload.data.code };
        }
    }, []);

    // AlarmDropdown 상태에 따른 미확인 알람 리스트 초기화
    useEffect(() => {
        isAlarmDropdown && resetUnConfirmedAlarmList();
    }, [isAlarmDropdown, resetUnConfirmedAlarmList]);

    // fcm token filtering 및 connect, toast message 설정
    useEffect(() => {
        // 카카오톡 인앱 브라우저 체크
        const isKakaoInApp = /KAKAOTALK/i.test(navigator.userAgent);

        // 첫 렌더링 시 오늘 날짜 기준 이전 알람 삭제
        removeOldAlarms();

        const handleMessage = (event: MessageEvent) => {
            if (event.data && event.data.command === 'setToast') {
                const payload = event.data;
                let toastData = {};

                if (payload.data) {
                    toastData = handleFCMFilter(payload) || {};
                }

                setToast(payload.title, payload.body, toastData);
            }
        };

        let unsubscribe: (() => void) | null = null;
        let mounted = true;

        // messaging 초기화 대기 및 onMessage 등록
        const setupMessaging = async () => {
            if (isKakaoInApp) {
                console.log('FCM setup skipped for KakaoTalk in-app browser');
                return;
            }

            try {
                const messagingInstance = await waitForMessaging();

                // 컴포넌트가 언마운트되었다면 설정하지 않음
                if (!mounted || !messagingInstance) {
                    return;
                }

                console.log('Setting up Firebase Messaging for foreground messages');
                unsubscribe = onMessage(messagingInstance, (payload: MessagePayload) => {
                    console.log('Foreground Message:', payload);
                    let toastData = {};

                    if (payload.data) {
                        toastData = handleFCMFilter(payload) || {};
                    }

                    if (payload.notification?.title && payload.notification?.body) {
                        setToast(payload.notification.title, payload.notification.body, toastData);
                    }
                });
            } catch (error) {
                console.error('Error setting up Firebase Messaging:', error);
            }
        };

        setupMessaging();

        // 카카오톡 인앱 브라우저가 아닐 때만 Service Worker 이벤트 리스너 등록
        if (!isKakaoInApp && 'serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleMessage);
        }

        // 클린업 함수
        return () => {
            mounted = false;
            if (!isKakaoInApp && 'serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('message', handleMessage);
            }
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [removeOldAlarms, setToast, handleFCMFilter]);

    // accountSetting 클릭 시 페이지 이동
    const clickAccountSetting = () => {
        if ((pathname === pagePaths.viewDetaildiagnosis && editState) || (pathname === pagePaths.addDiagnosis && movePageState)) {
            setClickType('header');
            setMovePath(pagePaths.accountSetting);
            return setIsLeaveModal();
        } else {
            // 사이드 메뉴 노출 때매 처리
            navigate(pagePaths.accountSetting, { state: { from: pathname } });
        }
    };

    return (
        <Layout>
            <HeaderContainer>
                <GlobalToast />
                {isAlarmDropdown && <TodayAlarmDropdown setIsAlarmDropdown={setIsAlarmDropdown} />}
                <HospitalAndNameWrapper>
                    <Text>{loginData.hospitalName}</Text>
                    <Divide />
                    <Text>
                        {loginData.name}
                        <TextWeight>님</TextWeight>
                    </Text>
                </HospitalAndNameWrapper>
                <IconsContainer>
                    <IconWrapper onClick={() => setIsLogoutModal(true)}>
                        <Logout />
                    </IconWrapper>
                    {/* TODO. 병원타입이 추가되어 타입으로 관리되어야됨. TLC심리상담센터, 테스트심리상담센터 추가로 칼로리랩 페이지에서 넘어온 경우 숨김처리*/}
                    {!isConsultationHospital(loginData.hospitalId) && !pathname.includes('cl') && (
                        <IconWrapper onClick={setIsAlarmDropdown}>{unConfirmedAlarmList.length !== 0 ? <UnConfirmAlarmIcon /> : !isAlarmDropdown ? <Alarm /> : <AlarmFill />}</IconWrapper>
                    )}
                    <IconWrapper onClick={clickAccountSetting}>
                        <SettingUser />
                    </IconWrapper>
                </IconsContainer>
            </HeaderContainer>
        </Layout>
    );
};

export default Header;

const Layout = styled.div`
    display: flex;
    position: sticky;
    top: 0;
    align-items: center;
    justify-content: right;
    height: 60px;
    width: calc(100vw - 332px);
    min-width: 950px; //1280px - 330px
    padding: 0 20px;
    background-color: ${palette.white};
    z-index: ${zIndex.basic};
`;

const HeaderContainer = styled.div`
    position: relative;
    display: flex;
    gap: 8px;
`;

const HospitalAndNameWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const Text = styled.span`
    display: flex;
    height: 19px;
    align-items: end;
    font-size: ${font.size[16]};
    font-weight: ${font.weight.semiBold};
`;

const TextWeight = styled.span`
    font-weight: ${font.weight.regular};
`;

const IconsContainer = styled.div`
    display: flex;
    gap: 2px;
`;

const IconWrapper = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 36px;
    height: 36px;
`;

const Divide = styled.div`
    width: 1px;
    height: 12px;
    background-color: ${palette.black};
    opacity: 0.3;
`;
